import {calculateSubscription, CalculateSubscriptionResult, getSubscription, SubscriptionItemResp} from "api/query/Subscription";
import { useCallback, useEffect, useState } from "react";
import {NavLink, useParams, useSearchParams} from "react-router-dom";
import { SectionContainer } from "../ui/grid";
import Title from "../ui/title/Title";
import SubcriptionItem from "./SubcriptionItem/SubcriptionItem";
import styles from './Subscription.module.scss'
import Layout from '../layout'
import {LoaderCircle} from "../payment/loader/LoaderCircle";
import { GrzHelper } from "helper/GrzHelper";
import { InfoType } from "../../api/query/Info";

const SubscriptionPage = () => {
	const { zone } = useParams();
	const [searchParams, setSearchParams] = useSearchParams();
	const grzParam = searchParams.get("grz");
	const phoneParam = searchParams.get("phone");
	const subscriptionParam = searchParams.get("subscription");
	const quantityParamRaw = Number(searchParams.get("quantity") || 1);
	const quantityParam = Number.isFinite(quantityParamRaw) ? Math.max(1, quantityParamRaw) : 1;
	const promoCodeParam = searchParams.get("promoCode") || '';
	const timestartParam = searchParams.get("timestart") || '';

	const [subscriptionV2EnabledState, setSubscriptionV2EnabledState] = useState<boolean>(false);
	const [selectedState, setSelectedState] = useState<string | null>(subscriptionParam);
	const [subscriptionResState, setSubscriptionResState] = useState<SubscriptionItemResp[]>([]);
	const [quantityByCodeState, setQuantityByCodeState] = useState<Record<string, number>>(subscriptionParam ? { [subscriptionParam]: quantityParam } : {});
	const [timestartByCodeState, setTimestartByCodeState] = useState<Record<string, string>>(subscriptionParam && timestartParam ? { [subscriptionParam]: timestartParam } : {});
	const [promoInputState, setPromoInputState] = useState<string>(promoCodeParam);
	const [appliedPromoCodeState, setAppliedPromoCodeState] = useState<string>(promoCodeParam);
	const [calculationState, setCalculationState] = useState<CalculateSubscriptionResult | null>(null);
	const [calculationLoadingState, setCalculationLoadingState] = useState<boolean>(false);
	const [calculationErrorState, setCalculationErrorState] = useState<string>('');
	const [extraGrzModalState, setExtraGrzModalState] = useState<boolean>(false);
	const [extraGrzInputState, setExtraGrzInputState] = useState<string>('');
	const [extraGrzListState, setExtraGrzListState] = useState<string[]>([]);
	const [mainGrzRemovedState, setMainGrzRemovedState] = useState<boolean>(false);
	const [draftExtraGrzListState, setDraftExtraGrzListState] = useState<string[]>([]);
	const [draftMainGrzRemovedState, setDraftMainGrzRemovedState] = useState<boolean>(false);
	const [extraGrzErrorState, setExtraGrzErrorState] = useState<string>('');
	const isSubscriptionPromoEnabled = subscriptionV2EnabledState;

	const handleInfoLoaded = useCallback((info: InfoType) => {
		setSubscriptionV2EnabledState(!!info.result.subscriptionV2Enabled);
	}, []);

	useEffect(() => {
		const hasGrz = !!(grzParam && grzParam !== '');
		const hasPhone = !!(phoneParam && phoneParam !== '');
		if (hasGrz || hasPhone) {
			getSubscription(zone, hasGrz ? grzParam : undefined, hasPhone ? phoneParam : undefined)
			.then((data) => {
				setSubscriptionResState(data.result || [])
			})
		}
	}, [zone, grzParam, phoneParam])

	const updateSelectionParams = (next: {
		subscription?: string | null;
		quantity?: number | null;
		promoCode?: string | null;
		timestart?: string | null;
	}) => {
		if (!isSubscriptionPromoEnabled) return;
		const params = new URLSearchParams(searchParams);
		const selectedCode = next.subscription !== undefined ? next.subscription : selectedState;
		if (selectedCode) {
			params.set('subscription', selectedCode);
		} else {
			params.delete('subscription');
		}
		if (next.quantity !== undefined) {
			const quantity = next.quantity ?? 1;
			quantity > 1 ? params.set('quantity', String(quantity)) : params.delete('quantity');
		}
		if (next.promoCode !== undefined) {
			const promoCode = next.promoCode ?? '';
			promoCode ? params.set('promoCode', promoCode) : params.delete('promoCode');
		}
		if (next.timestart !== undefined) {
			const timestart = next.timestart ?? '';
			timestart ? params.set('timestart', timestart) : params.delete('timestart');
		}
		setSearchParams(params, { replace: true });
	};

	const selectedSubscription = subscriptionResState.find((subscription) => subscription.code === selectedState);
	const selectedExtraGrzMaxCount = selectedSubscription?.extraGrzMaxCount ?? 10;
	const draftGrzList = [draftMainGrzRemovedState ? null : grzParam, ...draftExtraGrzListState].filter(Boolean) as string[];
	const selectedQuantity = selectedState ? (quantityByCodeState[selectedState] ?? 1) : 1;
	const selectedTimestart = selectedSubscription
		? (timestartByCodeState[selectedSubscription.code] ?? selectedSubscription.timestart)
		: '';
	const isPromoAppliedSuccessfully = !!appliedPromoCodeState
		&& isSubscriptionPromoEnabled
		&& promoInputState.trim() === appliedPromoCodeState
		&& !calculationLoadingState
		&& calculationState?.promoCodeInf?.code === 200
		&& (calculationState?.promoAppliedQuantity ?? 0) > 0;
	const promoCodeForPayment = isPromoAppliedSuccessfully ? appliedPromoCodeState : '';
	const formatRub = (value: number | undefined) => {
		if (typeof value !== 'number') return '-';
		return Number.isInteger(value / 100) ? String(value / 100) : (value / 100).toFixed(2).replace('.', ',');
	};
	const getDisplayAmount = (subscription: SubscriptionItemResp) => (
		isSubscriptionPromoEnabled && calculationState
			? formatRub(calculationState.amount)
			: String(subscription.price * (quantityByCodeState[subscription.code] ?? 1))
	);
	const getPaymentLink = (subscription: SubscriptionItemResp) => {
		const quantity = quantityByCodeState[subscription.code] ?? 1;
		const amount = calculationState?.amount ?? (subscription.price * quantity * 100);
		const timestart = timestartByCodeState[subscription.code] ?? subscription.timestart;
		const timeend = calculationState?.timeend ?? subscription.timeend;
		const subscriptionGrzList = [mainGrzRemovedState ? null : grzParam, ...extraGrzListState]
			.filter(Boolean)
			.slice(0, selectedExtraGrzMaxCount) as string[];
		const params = new URLSearchParams({
			grz: grzParam ?? '',
			phone: phoneParam ?? 'Не указан',
		});
		if (subscription.extraGrzEnabled && subscriptionGrzList.length > 0) {
			params.append('subscriptionGrz', subscriptionGrzList.join(','));
		}
		if (isSubscriptionPromoEnabled) {
			params.append('quantity', String(quantity));
			params.append('promoCode', promoCodeForPayment);
			params.append('amount', String(amount));
			params.append('timestart', timestart);
			params.append('timeend', timeend);
		}
		return `/${zone}/subscription/${subscription.code}/payment?${params.toString()}`;
	};
	const handleQuantityChange = (subscriptionCode: string, nextValue: number) => {
		const quantity = Math.max(1, nextValue);
		setQuantityByCodeState((state) => ({
			...state,
			[subscriptionCode]: quantity,
		}));
		updateSelectionParams({ subscription: subscriptionCode, quantity });
		setCalculationErrorState('');
		if (!isPromoAppliedSuccessfully) {
			setPromoInputState('');
			setAppliedPromoCodeState('');
			updateSelectionParams({ subscription: subscriptionCode, quantity, promoCode: '' });
		}
	};
	const isBuyButtonLoading = isSubscriptionPromoEnabled && calculationLoadingState;
	const toDatetimeLocalValue = (value: string) => value ? value.slice(0, 16) : '';
	const handleTimestartChange = (subscriptionCode: string, value: string) => {
		const normalized = value.length === 16 ? `${value}:00` : value;
		setTimestartByCodeState((state) => ({
			...state,
			[subscriptionCode]: normalized,
		}));
		updateSelectionParams({ subscription: subscriptionCode, timestart: normalized });
		setCalculationErrorState('');
	};
	const normalizeGrz = (value: string) => GrzHelper.clearRus(value).toUpperCase();
	const formatRusGrz = (value: string) => {
		const grzWithoutSpaces = GrzHelper.clearRus(value).toUpperCase();
		const grzArr = grzWithoutSpaces.match(/^([\s\S])([\s\S]{0,3})([\s\S]{0,2})([\s\S]{0,3})/);
		return (Array.isArray(grzArr) ? grzArr : []).filter((v, i) => (i > 0 && v)).join(' ');
	};
	const handleExtraGrzInputChange = (value: string) => {
		setExtraGrzInputState(formatRusGrz(value));
		setExtraGrzErrorState('');
	};
	const handleExtraGrzAdd = () => {
		const normalized = normalizeGrz(extraGrzInputState);
		if (!normalized || !GrzHelper.testRus(normalized)) {
			setExtraGrzErrorState('Некорректный формат госномера');
			return;
		}
		if (draftGrzList.includes(normalized)) {
			setExtraGrzErrorState('Этот госномер уже добавлен');
			return;
		}
		if (draftGrzList.length >= selectedExtraGrzMaxCount) {
			setExtraGrzErrorState(`Можно добавить не больше ${selectedExtraGrzMaxCount} госномеров`);
			return;
		}
		if (normalized === grzParam) {
			setDraftMainGrzRemovedState(false);
			setExtraGrzInputState('');
			setExtraGrzErrorState('');
			return;
		}
		setDraftExtraGrzListState((state) => [...state, normalized]);
		setExtraGrzInputState('');
		setExtraGrzErrorState('');
	};
	const handleExtraGrzRemove = (value: string) => {
		if (value === grzParam) {
			setDraftMainGrzRemovedState(true);
		} else {
			setDraftExtraGrzListState((state) => state.filter((item) => item !== value));
		}
		setExtraGrzErrorState('');
	};
	const handleSavedGrzRemove = (value: string) => {
		if (value === grzParam) {
			setMainGrzRemovedState(true);
		} else {
			setExtraGrzListState((state) => state.filter((item) => item !== value));
		}
	};
	const openExtraGrzModal = () => {
		setDraftExtraGrzListState(extraGrzListState);
		setDraftMainGrzRemovedState(mainGrzRemovedState);
		setExtraGrzInputState('');
		setExtraGrzErrorState('');
		setExtraGrzModalState(true);
	};
	const closeExtraGrzModal = () => {
		if (draftGrzList.length > 0) {
			setExtraGrzListState(draftExtraGrzListState);
			setMainGrzRemovedState(draftMainGrzRemovedState);
		}
		setExtraGrzInputState('');
		setExtraGrzErrorState('');
		setExtraGrzModalState(false);
	};
	const saveExtraGrzModal = () => {
		if (draftGrzList.length === 0 || extraGrzErrorState) return;
		setExtraGrzListState(draftExtraGrzListState);
		setMainGrzRemovedState(draftMainGrzRemovedState);
		closeExtraGrzModal();
	};

	const runCalculateSubscription = (promoCode: string) => {
		if (!isSubscriptionPromoEnabled || !selectedSubscription || !grzParam) return;
		setCalculationLoadingState(true);
		setCalculationErrorState('');
		calculateSubscription(zone, {
			grz: grzParam,
			phone: phoneParam ?? 'Не указан',
			promoCode,
			permissionKindCode: selectedSubscription.code,
			quantity: selectedQuantity,
			timestart: selectedTimestart,
		})
			.then((data) => {
				const result = data.result;
				if (!result || !result.ok || typeof result.amount !== 'number') {
					setCalculationState(null);
					setCalculationErrorState('Не удалось рассчитать стоимость.');
					return;
				}
				setCalculationState(result);
			})
			.catch(() => {
				setCalculationState(null);
				setCalculationErrorState('Не удалось рассчитать стоимость.');
			})
			.finally(() => {
				setCalculationLoadingState(false);
			});
	};

	useEffect(() => {
		if (isSubscriptionPromoEnabled && selectedSubscription) {
			runCalculateSubscription(appliedPromoCodeState);
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedState, selectedQuantity, selectedTimestart, appliedPromoCodeState])

	return (
		<Layout infoListener={handleInfoLoaded}>
			<>
				<SectionContainer>
					<Title text="Выберите абонемент" />
					{ Array.isArray(subscriptionResState) && subscriptionResState.length > 0 ? (
						<>
							<div className={styles.items}>
								{subscriptionResState.map((subscription) =>
									<div key={subscription.code} className={styles.item_wrap}>
										<SubcriptionItem
										elClass={`${subscription.code}`}
										title={subscription.name}
										subtitle={subscription.description}
										price={subscription.price}
										changed={() => {
											if (selectedState !== subscription.code) {
												setSelectedState(subscription.code);
												setPromoInputState('');
												setAppliedPromoCodeState('');
												setCalculationState(null);
												setCalculationErrorState('');
												setExtraGrzModalState(false);
												setExtraGrzInputState('');
												setExtraGrzErrorState('');
												setMainGrzRemovedState(false);
												updateSelectionParams({
													subscription: subscription.code,
													quantity: quantityByCodeState[subscription.code] ?? 1,
													promoCode: '',
													timestart: timestartByCodeState[subscription.code] ?? subscription.timestart,
												});
											}
										}}
										isSelected={selectedState === subscription.code}
										quantity={quantityByCodeState[subscription.code] ?? 1}
										onQuantityMinus={() => handleQuantityChange(subscription.code, (quantityByCodeState[subscription.code] ?? 1) - 1)}
										onQuantityPlus={() => handleQuantityChange(subscription.code, (quantityByCodeState[subscription.code] ?? 1) + 1)}
										mainGrz={mainGrzRemovedState ? null : grzParam}
										extraGrzList={extraGrzListState}
										extraGrzEnabled={isSubscriptionPromoEnabled && !!subscription.extraGrzEnabled}
										extraGrzMaxCount={subscription.extraGrzMaxCount}
										excludingCount={subscription.excludingCount}
										onExtraGrzClick={openExtraGrzModal}
										onGrzRemove={handleSavedGrzRemove}
										showExtendedControls={isSubscriptionPromoEnabled}
										timestartValue={toDatetimeLocalValue(timestartByCodeState[subscription.code] ?? subscription.timestart)}
										timeendValue={selectedState === subscription.code && calculationState?.timeend ? calculationState.timeend : subscription.timeend}
										showDateControl={!subscription.fixtimestart || !subscription.fixtimeend}
										onTimestartChange={(value) => handleTimestartChange(subscription.code, value)}
										promoInput={promoInputState}
										appliedPromoCode={appliedPromoCodeState}
										onPromoInputChange={(value) => {
											setPromoInputState(value);
											setCalculationErrorState('');
											if (value.trim() !== appliedPromoCodeState) {
												setAppliedPromoCodeState('');
												updateSelectionParams({ subscription: subscription.code, promoCode: '' });
											}
										}}
										onPromoApply={() => {
											const promoCode = promoInputState.trim();
											setAppliedPromoCodeState(promoCode);
											updateSelectionParams({ subscription: subscription.code, promoCode });
										}}
										onPromoCancel={() => {
											setPromoInputState('');
											setAppliedPromoCodeState('');
											setCalculationErrorState('');
											updateSelectionParams({ subscription: subscription.code, promoCode: '' });
										}}
										calculation={selectedState === subscription.code ? calculationState : null}
										calculationLoading={selectedState === subscription.code && calculationLoadingState}
										calculationError={selectedState === subscription.code ? calculationErrorState : ''}
										isPromoAppliedSuccessfully={selectedState === subscription.code && isPromoAppliedSuccessfully}
										/>
										{selectedState === subscription.code ? (
											<NavLink
											className={`${styles.btn} ${isBuyButtonLoading ? styles.btn_disabled : ''}`}
											to={getPaymentLink(subscription)}
											onClick={(event) => {
												if (isBuyButtonLoading) event.preventDefault();
											}}
										>
											{isBuyButtonLoading ? <span className={styles.btn_spinner} /> : null}
											<span>{isBuyButtonLoading ? 'Считаем' : 'Купить'}</span>
											{isSubscriptionPromoEnabled && !isBuyButtonLoading ? <b>{getDisplayAmount(subscription)} руб.</b> : null}
											</NavLink>
										) : null}
									</div>
								)}
							</div>
						</>
					) : <LoaderCircle /> }
				</SectionContainer>
				{extraGrzModalState ? (
				<div className={styles.grz_modal_overlay} onMouseDown={closeExtraGrzModal}>
					<div className={styles.grz_modal} onMouseDown={(event) => event.stopPropagation()}>
						<button type="button" className={styles.grz_modal_close} onClick={closeExtraGrzModal}>
							×
						</button>
						<div className={styles.grz_modal_title}>Госномера абонемента</div>
						<div className={styles.grz_modal_subtitle}>
							Добавьте госномера автомобилей для этого абонемента.
						</div>
						<div className={styles.grz_modal_count}>Добавлено {draftGrzList.length} из {selectedExtraGrzMaxCount}</div>
						<div className={styles.grz_modal_list}>
							{draftGrzList.map((grz) => (
								<div className={styles.grz_modal_item} key={grz}>
									<div>
										<b>{grz}</b>
									</div>
									<button type="button" onClick={() => handleExtraGrzRemove(grz)}>Удалить</button>
								</div>
							))}
						</div>
						<div className={styles.grz_modal_add}>
							<input
								type="text"
								value={extraGrzInputState}
								onChange={(event) => handleExtraGrzInputChange(event.target.value)}
								placeholder="X 000 XX 00"
								onKeyDown={(event) => {
									if (event.key === 'Enter') handleExtraGrzAdd();
								}}
							/>
							<button type="button" onClick={handleExtraGrzAdd}>Добавить</button>
						</div>
						{extraGrzErrorState ? <div className={styles.grz_modal_error}>{extraGrzErrorState}</div> : null}
						<button
							type="button"
							className={styles.grz_modal_done}
							onClick={saveExtraGrzModal}
							disabled={draftGrzList.length === 0 || !!extraGrzErrorState}
						>
							Готово
						</button>
					</div>
				</div>
				) : null}
			</>
		</Layout>
	)
}

export default SubscriptionPage;
